import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import Text from './Text';
import combineStyles from '../utils/combineStyles';
import getThemeLookup from '../selectors/getThemeLookup';
import { CONTAINER_PROPERTIES } from '../utils/theme';
import PaymentMethodProvider from './PaymentMethodProvider';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import debug from '../utils/debug';
import type { PaymentMethod } from '@stripe/stripe-js';
import { setPaymentFormFilled } from '../slices/paymentFormFilled';
import { loadStripe } from '@stripe/stripe-js';
import { useFormikContext } from 'formik';
import { processPayment, process3ds } from '../thunks/processPayment';
import { enqueueErrorSnackbar } from '../utils/snackbar';
import { setPaymentFormInUse } from '../slices/paymentFormInUse';
import getProfile from '../selectors/getProfile';
import { OrderingSelectors, OrderingConstants, OrderingOperations } from 'polygon-ordering';
import { TYRO_CONSTANTS, useTyro } from '../hooks/TyroProvider';
import ContentLoader from 'react-content-loader';
import constructGiftCardTopUpPaymentRequest from '../utils/constructGiftCardTopUpPaymentRequest';
import SaveCardSwitch from './SavedCardSwitch';
import { RiLockLine } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';

const { getCalculatedGiftcardSurcharge, getGatewayType, getMember, getPaymentGatewayConfig } =
  OrderingSelectors;
const { PAYMENT_GATEWAY_TYPES, TYRO_PAYMENT_TOKEN_STATUS, PAYMENT_METHOD } = OrderingConstants;
const { validateGiftCardTopUpSale } = OrderingOperations;

const CreditCardProvider = ({ route }: { route: string }) => {
  const dispatch = useAppDispatch();
  const paymentMethod = useAppSelector(state => state.paymentMethod);
  const p = useAppSelector(getThemeLookup);
  const { t } = useTranslation();
  const gatewayType = useAppSelector(getGatewayType);
  // eslint-disable-next-line
  const stripe = gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE ? useStripe() : null;
  // eslint-disable-next-line
  const elements = gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE ? useElements() : null;
  const paymentFormInUse = useAppSelector(state => state.paymentFormInUse);
  const paymentFormFilled = useAppSelector(state => state.paymentFormFilled);

  const [token, setToken] = useState<PaymentMethod | null>(null);
  const formik = useFormikContext<FormikFields>();

  const paymentGatewayConfig = useAppSelector(getPaymentGatewayConfig);
  const hqGatewayKey = useAppSelector(state => state.config.hqGatewayKey);
  const merchantAccount = useAppSelector(state => state.config.stripeMerchantAccount);
  const stripeAccountToUse = paymentGatewayConfig?.merchantAccount || merchantAccount;

  const profile = useAppSelector(getProfile);
  const member = useAppSelector(getMember);

  const { values } = formik;
  const surcharge = useAppSelector(state =>
    getCalculatedGiftcardSurcharge(state, Number(values.Amount)),
  );

  // eslint-disable-next-line
  const tyroData = gatewayType === PAYMENT_GATEWAY_TYPES.TYRO ? useTyro() : null;
  const {
    payRequestReady,
    payFormReady,
    loading,
    submitTyroPay,
    setTyroPaymentOrderId,
    giftCardTopUpData,
  } = gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && tyroData;

  const giftcardRoutes = [
    'memberPurchaseGiftCardForOthers',
    'nonMemberPurchaseGiftCardForOthers',
    'memberPurchaseGiftCardForSelf',
    'nonMemberPurchaseGiftCardForSelf',
  ];

  // Need to add logic for gift card and top up pay
  const handleTyroPaySubmit = async () => {
    try {
      if (giftCardTopUpData) {
        if (!giftCardTopUpData?.uuid) {
          throw new Error('Something went wrong, cannot make the payment without uuid');
        }

        const { body } = constructGiftCardTopUpPaymentRequest(
          giftCardTopUpData as unknown as ProcessPaymentData,
        );

        const { valid, errorText } = await dispatch(
          validateGiftCardTopUpSale({
            uuid: giftCardTopUpData?.uuid,
            route: giftcardRoutes.includes(route) ? 'giftcard' : 'topup',
            giftCardTopUpBody: {
              ...body,
              UUID: giftCardTopUpData.uuid,
            },
            authenticationMethod: member ? 'member' : 'none',
          }),
        ).unwrap();
        if (valid) {
          const response = await submitTyroPay();
          if (response.status === TYRO_PAYMENT_TOKEN_STATUS.SUCCESS) {
            setTyroPaymentOrderId(response.origin.orderId);
          } else {
            throw new Error(response.error);
          }
        } else {
          throw new Error(errorText);
        }
      }
    } catch (err) {
      //@ts-ignore
      enqueueErrorSnackbar(err);
      dispatch(setPaymentFormInUse(false));
    }
  };

  const handleFormSubmit = () => {
    formik.setSubmitting(true);
    const data = {
      values: formik.values,
      token,
      route,
      giftcardSurcharge: surcharge.value,
    };

    dispatch(processPayment(data)).then((response: any) => {
      if (response.meta.requestStatus === 'fulfilled') {
        formik.resetForm();
      } else {
        if (response.payload.details.json.error_code === 206) {
          const { modal } = response.payload;
          const { additional_info } = response.payload.details.json;
          handle3ds(additional_info, modal); // 3ds required
        }
      }
      formik.setSubmitting(false);
    });
  };

  useEffect(() => {
    if (token) {
      //submit with a credit card
      handleFormSubmit();
    }
  }, [token]);

  const handle3ds = async (info: any, modal: string) => {
    try {
      const { orderInfo } = info;

      // We want to use the HQ account for handling 3ds when doing a credit card payment for gift card/top up.
      // Previously, the default account for all clients was HQ, but that’s no longer always the case.
      // Since the default account can vary, we should explicitly use the HQ account instead.
      // Concept Ate default account is not HQ

      const stripe2 = await loadStripe(hqGatewayKey!, { stripeAccount: stripeAccountToUse! });
      const result = await stripe2?.handleCardAction(info.action.client_secret);

      if (result) {
        const { paymentIntent, error } = result;

        if (error) {
          dispatch(setPaymentFormInUse(false));
          enqueueErrorSnackbar(error.message!);
          return;
        }

        await dispatch(
          process3ds({
            orderInfo,
            actionResponse: {
              payment_intent_id: paymentIntent?.id,
            },
            modal,
          }),
        ).then((res: any) => {
          if (res.type === 'process3ds/fulfilled') {
            formik.resetForm();
          }
        });
      }
    } catch (err) {
      //@ts-ignore
      enqueueErrorSnackbar(err);
    }
  };

  //submit sale
  useEffect(() => {
    if (paymentFormInUse && paymentMethod?.method === PAYMENT_METHOD.CREDIT_CARD) {
      if (gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE) handleSubmit();
      else if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO) handleTyroPaySubmit();
    }
  }, [paymentFormInUse]);

  const handleSubmit = async () => {
    const cardElement = elements?.getElement(CardElement);

    if (cardElement) {
      await stripe
        ?.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: profile?.givenNames + ' ' + profile?.surname,
            email: profile?.emailAddress,
            phone: profile?.phoneNumber,
          },
        })
        .then(({ paymentMethod, error }) => {
          debug({ paymentMethod, error });

          if (error) {
            enqueueErrorSnackbar((error as any).message);
            dispatch(setPaymentFormInUse(false));
          }
          if (paymentMethod) {
            setToken(paymentMethod);
          }
        })
        .catch(e => {
          debug('credit card payment error', e);
          throw e;
        });
    }
  };

  const alreadySelected = paymentMethod?.method === PAYMENT_METHOD.CREDIT_CARD ? true : false;

  if (gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE && (!stripe || !elements)) {
    return null;
  }

  const displayCreditCardForm = !loading && payRequestReady && payFormReady && alreadySelected;

  return (
    <PaymentMethodProvider
      method={PAYMENT_METHOD.CREDIT_CARD}
      right={
        <div>
          <RiLockLine style={{ fontWeight: 'bolder', marginRight: 4 }} size={18} color="#737373" />
          <span>
            <Text
              style={{
                fontSize: 14,
                color: '#737373',
              }}
              value={t('securePayment')}
            />
          </span>
        </div>
      }
      subtitle={<Text themeKey="paymentMethodUnavailableReason">&nbsp;</Text>}
    >
      <div style={styles.contentContainer}>
        {gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE && (
          <div style={combineStyles(styles.input, p('input', CONTAINER_PROPERTIES))}>
            <CardElement
              options={{
                hidePostalCode: true,
                style: {
                  base: {
                    backgroundColor: 'transparent', // workaround for issue with darkmode
                    color: p('stripeElementText', ['color']).color,
                  },
                },
              }}
              onChange={event => {
                if (event.complete) {
                  dispatch(setPaymentFormFilled(true));
                } else {
                  if (paymentFormFilled) {
                    dispatch(setPaymentFormFilled(false));
                  }
                }
              }}
            />
          </div>
        )}

        {gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && (
          <>
            <div
              id={TYRO_CONSTANTS.CREDIT_CARD_PAY_HTML_ID}
              style={{
                display: displayCreditCardForm ? 'block' : 'none',
                margin: '0 -8px',
              }}
            ></div>

            {alreadySelected && loading && (
              <ContentLoader
                speed={2}
                width="100%"
                height="100%"
                viewBox="0 0 500 240"
                backgroundColor="#f3f3f3"
                foregroundColor="#ecebeb"
              >
                <rect x="8" y="0" rx="3" ry="3" width="484" height="40" />
                <rect x="8" y="70" rx="3" ry="3" width="484" height="40" />
                <rect x="8" y="140" rx="3" ry="3" width="233" height="40" />
                <rect x="257" y="140" rx="3" ry="3" width="233" height="40" />
              </ContentLoader>
            )}
          </>
        )}

        {alreadySelected &&
          (gatewayType !== PAYMENT_GATEWAY_TYPES.TYRO || displayCreditCardForm) && (
            <SaveCardSwitch />
          )}
      </div>
    </PaymentMethodProvider>
  );
};

const styles = {
  contentContainer: {
    minHeight: 57,
    marginBottom: 20,
  },
  input: {
    borderRadius: 4,
    border: '1px solid #c4c4c4',
    marginBottom: 20,
  },
};

export default CreditCardProvider;
